import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './Inventory.css';
import './Battle.css';
import './Market.css';
import AuthLayeredCard from './AuthLayeredCard';
import { authButtonSmallStyle } from '../constants/authStyles';
import { buildDetailedStatRows } from '../utils/itemStatUtils';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import isGridItemEquipped from './inventory/isGridItemEquipped';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import { ItemImage } from './inventory/inventoryBagGridUtils';
import InventoryItemPopup from './inventory/InventoryItemPopup';
import useInventoryItemPopupActions from './inventory/useInventoryItemPopupActions';

const INVENTORY_TYPE_CONFIG = [
  { inventoryType: 'weapons', slotType: 'weapon', label: 'Silah', baseKey: 'weaponId' },
  { inventoryType: 'armors', slotType: 'armor', label: 'Zırh', baseKey: 'armorId' },
  { inventoryType: 'helmets', slotType: 'helmet', label: 'Kask', baseKey: 'helmetId' },
  { inventoryType: 'boots', slotType: 'boot', label: 'Ayakkabı', baseKey: 'bootId' },
  { inventoryType: 'earrings', slotType: 'earring', label: 'Küpe', baseKey: 'earringId' },
  { inventoryType: 'necklaces', slotType: 'necklace', label: 'Kolye', baseKey: 'necklaceId' },
  { inventoryType: 'shields', slotType: 'shield', label: 'Kalkan', baseKey: 'shieldId' },
  { inventoryType: 'pots', slotType: 'pot', label: 'Pot', baseKey: 'potId' },
  { inventoryType: 'others', slotType: 'other', label: 'Diğer', baseKey: 'otherId' }
];

const GRID_PAGE_SIZE = 30;
const GRID_COLUMNS = 6;
const GRID_ROWS = 5;
const INVENTORY_PAGE_COUNT = 4;
const INVENTORY_GRID_SIZE = GRID_PAGE_SIZE * INVENTORY_PAGE_COUNT;
const INVENTORY_DEFAULT_UNLOCKED_SLOTS = 60;
const MARKET_GRID_SIZE = 30;
const marketMenuBgCssVars = (() => {
  const p = process.env.PUBLIC_URL ?? '';
  return {
    '--inv-slot-bg': `url(${p}/image/menu/kutuuzun.png)`,
    '--inv-adam-bg': `url(${p}/image/menu/adam.png)`,
    '--inv-kutukare-bg': `url(${p}/image/menu/kutukare.png)`
  };
})();

const getBaseItemId = (item) => (
  item?.weaponId ||
  item?.armorId ||
  item?.helmetId ||
  item?.bootId ||
  item?.earringId ||
  item?.necklaceId ||
  item?.shieldId ||
  item?.potId ||
  item?.otherId ||
  item?.catalogItemId
);

const buildItemImageUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')) return rawUrl;
  return rawUrl;
};

const emptyGrid = (size) => Array(size).fill(null);

const SLOT_LABEL_KEYS = {
  weapon: 'slotWeapon',
  armor: 'slotArmor',
  helmet: 'slotHelmet',
  boot: 'slotBoot',
  earring: 'slotEarring',
  necklace: 'slotNecklace',
  shield: 'slotShield',
  pot: 'slotPot',
  other: 'slotOther',
};

const Market = () => {
  const navigate = useNavigate();
  const { t: tm } = useTranslation('market');
  const { t: tc } = useTranslation('common');
  const { t: ti } = useTranslation('inventory');
  const username = localStorage.getItem('username');
  const browseRef = useRef(null);
  const marketCardRef = useRef(null);
  const inventoryGridAreaRef = useRef(null);
  const touchStartPointRef = useRef({ x: 0, y: 0 });
  const touchHasMovedRef = useRef(false);
  const touchDragSourceRef = useRef(null);
  const touchScrollSnapshotRef = useRef(null);
  const skipNextClickRef = useRef(false);

  const [activeTab, setActiveTab] = useState('mine');
  const [isRefreshing, setIsRefreshing] = useState(true);
  const { setError, setStatus, clearError } = usePopupMessage();
  const { refreshCharacter, setDepotItemCount } = useCharacter();
  const [market, setMarket] = useState(null);
  const [marketName, setMarketName] = useState('');
  const [myMarketSlots, setMyMarketSlots] = useState(emptyGrid(MARKET_GRID_SIZE));
  const [inventorySlots, setInventorySlots] = useState(emptyGrid(INVENTORY_GRID_SIZE));
  const [unlockedSlotsCount, setUnlockedSlotsCount] = useState(INVENTORY_DEFAULT_UNLOCKED_SLOTS);
  const [currentInventoryPage, setCurrentInventoryPage] = useState(1);
  const [catalogs, setCatalogs] = useState({});
  const [draggedInventoryIndex, setDraggedInventoryIndex] = useState(null);
  const [draggedMarketIndex, setDraggedMarketIndex] = useState(null);
  const [dragOverMarketIndex, setDragOverMarketIndex] = useState(null);
  const [dragOverInventoryIndex, setDragOverInventoryIndex] = useState(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchDragGhost, setTouchDragGhost] = useState(null);
  const [pendingList, setPendingList] = useState(null);
  const [listPrice, setListPrice] = useState('');
  const [listCount, setListCount] = useState(1);
  const [activePopup, setActivePopup] = useState(null);
  const [browseMarkets, setBrowseMarkets] = useState([]);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseLoaded, setBrowseLoaded] = useState(false);
  const [hasMoreMarkets, setHasMoreMarkets] = useState(true);
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopSlots, setShopSlots] = useState(emptyGrid(MARKET_GRID_SIZE));
  const [buyerGold, setBuyerGold] = useState(0);
  const isMarketOpen = Boolean(market?.isOpen);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const marketFreeSlots = Number(market?.freeSlots) || 12;
  const permitUntil = Number(market?.permitUntil) || 0;
  const permitActive = permitUntil > nowSec;
  const closesAt = Number(market?.closesAt) || 0;
  const openLeftSec = isMarketOpen && closesAt > 0 ? Math.max(0, closesAt - nowSec) : 0;
  const isPermitLockedSlot = (index) => index >= marketFreeSlots && !permitActive;

  const lockTouchDragScroll = () => {
    if (touchScrollSnapshotRef.current) return;
    touchScrollSnapshotRef.current = {
      bodyOverflow: document.body.style.overflow,
      bodyTouchAction: document.body.style.touchAction
    };
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  const unlockTouchDragScroll = () => {
    const snapshot = touchScrollSnapshotRef.current;
    if (!snapshot) return;
    document.body.style.overflow = snapshot.bodyOverflow;
    document.body.style.touchAction = snapshot.bodyTouchAction;
    touchScrollSnapshotRef.current = null;
  };

  const getCategoryLabel = useCallback((slotType) => {
    return ti(SLOT_LABEL_KEYS[slotType] || 'slotOther');
  }, [ti]);

  const combineListing = useCallback((listing, sourceCatalogs = catalogs) => {
    const config = INVENTORY_TYPE_CONFIG.find((entry) => entry.inventoryType === listing.inventoryType);
    const baseItem = (sourceCatalogs[listing.inventoryType] || []).find((item) => item.id === listing.catalogItemId);
    if (!config || !baseItem) return null;
    return {
      ...listing,
      uniqueKey: `market-${listing.id}`,
      slotType: config.slotType,
      categoryLabel: getCategoryLabel(config.slotType),
      baseItem,
      count: listing.count || 1
    };
  }, [catalogs, getCategoryLabel]);

  const getItemGridSize = (slotItem) => {
    if (slotItem?.slotType === 'armor') {
      return { width: 1, height: 2 };
    }
    const rawW = Number(slotItem?.baseItem?.gridWidth || 1);
    const rawH = Number(slotItem?.baseItem?.gridHeight || 1);
    const width = Number.isFinite(rawW) ? Math.max(1, Math.min(rawW, GRID_COLUMNS)) : 1;
    const height = Number.isFinite(rawH) ? Math.max(1, Math.min(rawH, GRID_ROWS)) : 1;
    return { width, height };
  };

  const getFootprintIndexes = (anchorIndex, width, height, gridSize) => {
    if (!Number.isInteger(anchorIndex) || anchorIndex < 0 || anchorIndex >= gridSize) return null;
    const pageStart = Math.floor(anchorIndex / GRID_PAGE_SIZE) * GRID_PAGE_SIZE;
    const localIndex = anchorIndex - pageStart;
    const row = Math.floor(localIndex / GRID_COLUMNS);
    const col = localIndex % GRID_COLUMNS;
    if (col + width > GRID_COLUMNS || row + height > GRID_ROWS) return null;

    const indexes = [];
    for (let dy = 0; dy < height; dy += 1) {
      for (let dx = 0; dx < width; dx += 1) {
        indexes.push(pageStart + ((row + dy) * GRID_COLUMNS) + col + dx);
      }
    }
    return indexes.every((idx) => idx < gridSize) ? indexes : null;
  };

  const computePageCoverage = (pageSlots, pageStartIndex, gridSize) => {
    const coveredIndexes = new Set();
    const spanByIndex = {};
    pageSlots.forEach((slotItem, localIndex) => {
      if (!slotItem) return;
      const absoluteIndex = pageStartIndex + localIndex;
      if (coveredIndexes.has(absoluteIndex)) return;
      const { width, height } = getItemGridSize(slotItem);
      const footprint = getFootprintIndexes(absoluteIndex, width, height, gridSize);
      if (!footprint) {
        spanByIndex[absoluteIndex] = { width: 1, height: 1 };
        return;
      }
      spanByIndex[absoluteIndex] = { width, height };
      footprint.slice(1).forEach((idx) => coveredIndexes.add(idx));
    });
    return { coveredIndexes, spanByIndex };
  };

  const isSlotLocked = (index, unlockedCount = unlockedSlotsCount) => (
    Number.isInteger(index) && index >= unlockedCount
  );

  const canPlaceInGrid = (gridSlots, targetIndex, movingItem, gridSize, unlockedCount = unlockedSlotsCount) => {
    if (!movingItem || targetIndex < 0 || targetIndex >= gridSize) return false;
    const { width, height } = getItemGridSize(movingItem);
    const targetFootprint = getFootprintIndexes(targetIndex, width, height, gridSize);
    if (!targetFootprint) return false;
    return targetFootprint.every((idx) => !gridSlots[idx] && !isSlotLocked(idx, unlockedCount));
  };

  const computeGridCoverage = (gridSlots, gridSize) => {
    const coveredIndexes = new Set();
    const spanByIndex = {};
    const pageCount = Math.ceil(gridSize / GRID_PAGE_SIZE);
    for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
      const pageStart = pageIndex * GRID_PAGE_SIZE;
      const pageSlots = gridSlots.slice(pageStart, pageStart + GRID_PAGE_SIZE);
      const pageCoverage = computePageCoverage(pageSlots, pageStart, gridSize);
      Object.entries(pageCoverage.spanByIndex).forEach(([idx, span]) => {
        spanByIndex[Number(idx)] = span;
      });
      pageCoverage.coveredIndexes.forEach((idx) => coveredIndexes.add(idx));
    }
    return { coveredIndexes, spanByIndex };
  };

  const canMoveItemInGrid = (
    gridSlots,
    sourceIndex,
    targetIndex,
    gridSize,
    unlockedCount = unlockedSlotsCount
  ) => {
    if (!Number.isInteger(sourceIndex) || !Number.isInteger(targetIndex)) return false;
    if (sourceIndex === targetIndex) return false;
    const movingItem = gridSlots[sourceIndex];
    if (!movingItem) return false;

    const { coveredIndexes, spanByIndex } = computeGridCoverage(gridSlots, gridSize);
    const { width, height } = getItemGridSize(movingItem);
    const targetFootprint = getFootprintIndexes(targetIndex, width, height, gridSize);
    if (!targetFootprint) return false;

    const sourceSpan = spanByIndex[sourceIndex] || { width: 1, height: 1 };
    const sourceIndexes = getFootprintIndexes(sourceIndex, sourceSpan.width, sourceSpan.height, gridSize) || [sourceIndex];
    const sourceFootprint = new Set(sourceIndexes);

    return targetFootprint.every((idx) => {
      if (isSlotLocked(idx, unlockedCount)) return false;
      if (sourceFootprint.has(idx)) return true;
      if (gridSlots[idx]) return false;
      if (coveredIndexes.has(idx)) return false;
      return true;
    });
  };

  const resolveDraggedInventoryIndex = (event) => {
    if (draggedInventoryIndex !== null) return draggedInventoryIndex;
    if (!event?.dataTransfer) return null;

    const source = event.dataTransfer.getData('text/market-source');
    if (source !== 'inventory') return null;

    const payloadIndex = Number(event.dataTransfer.getData('text/inventory-index'));
    if (Number.isInteger(payloadIndex) && payloadIndex >= 0) return payloadIndex;

    const payloadKey = event.dataTransfer.getData('text/inventory-item-key');
    if (!payloadKey) return null;
    const resolvedIndex = inventorySlots.findIndex((item) => item?.uniqueKey === payloadKey);
    return resolvedIndex >= 0 ? resolvedIndex : null;
  };

  const persistInventoryLayout = async (gridSlots) => {
    if (!username) return;
    const slotsPayload = gridSlots
      .map((slotItem, index) => (
        slotItem && !isSlotLocked(index) ? { slotIndex: index, itemKey: slotItem.uniqueKey } : null
      ))
      .filter(Boolean);
    try {
      await axios.post('/api/inventory/layout', {
        username,
        slots: slotsPayload
      });
    } catch {
      setError(ti('errorLayoutSaveFailed'));
    }
  };

  const setNativeDragGhost = (event, imageUrl) => {
    if (!event?.dataTransfer || !imageUrl) return;
    const ghostImage = document.createElement('img');
    ghostImage.src = imageUrl;
    ghostImage.width = 48;
    ghostImage.height = 48;
    ghostImage.style.position = 'absolute';
    ghostImage.style.top = '-1000px';
    ghostImage.style.left = '-1000px';
    ghostImage.style.opacity = '0.9';
    document.body.appendChild(ghostImage);
    event.dataTransfer.setDragImage(ghostImage, 24, 24);
    setTimeout(() => {
      if (ghostImage.parentNode) {
        ghostImage.parentNode.removeChild(ghostImage);
      }
    }, 0);
  };

  const buildGridItems = useCallback((items, size, sourceCatalogs = catalogs) => {
    const next = emptyGrid(size);
    items.forEach((raw) => {
      const item = combineListing(raw, sourceCatalogs);
      if (item && item.slotIndex >= 0 && item.slotIndex < size) {
        next[item.slotIndex] = item;
      }
    });
    return next;
  }, [catalogs, combineListing]);

  const buildInventoryItemsFromPayload = useCallback((inventoryByType, sourceCatalogs) => (
    INVENTORY_TYPE_CONFIG.flatMap((config) => {
      const userItems = inventoryByType[config.inventoryType] || [];
      return userItems.map((item) => {
        const baseItem = (sourceCatalogs[config.inventoryType] || []).find((base) => base.id === getBaseItemId(item));
        if (!baseItem) return null;
        return {
          ...item,
          uniqueKey: `${config.inventoryType}-${item.id}`,
          inventoryType: config.inventoryType,
          slotType: config.slotType,
          categoryLabel: getCategoryLabel(config.slotType),
          baseItem,
          count: item.count || 1
        };
      }).filter(Boolean);
    })
  ), [getCategoryLabel]);

  const applyLayoutToGrid = useCallback((items, layoutData) => {
    const grid = emptyGrid(INVENTORY_GRID_SIZE);
    const usedKeys = new Set();
    let activeUnlockedCount = unlockedSlotsCount;
    if (layoutData?.unlockedSlots != null) {
      activeUnlockedCount = Math.min(
        INVENTORY_GRID_SIZE,
        Math.max(INVENTORY_DEFAULT_UNLOCKED_SLOTS, Number(layoutData.unlockedSlots) || INVENTORY_DEFAULT_UNLOCKED_SLOTS)
      );
      setUnlockedSlotsCount(activeUnlockedCount);
    }
    (layoutData?.slots || []).forEach((slot) => {
      const item = items.find((entry) => entry.uniqueKey === slot.itemKey);
      if (item && slot.slotIndex >= 0 && slot.slotIndex < INVENTORY_GRID_SIZE && !isSlotLocked(slot.slotIndex, activeUnlockedCount)) {
        grid[slot.slotIndex] = item;
        usedKeys.add(item.uniqueKey);
      }
    });
    let cursor = 0;
    items.filter((item) => !usedKeys.has(item.uniqueKey)).forEach((item) => {
      while (cursor < activeUnlockedCount && grid[cursor]) cursor += 1;
      if (cursor < activeUnlockedCount) {
        grid[cursor] = item;
        cursor += 1;
      }
    });
    return grid;
  }, [unlockedSlotsCount]);

  const applyMarketPayload = useCallback((data, { includeCatalogs = false } = {}) => {
    const nextCatalogs = includeCatalogs ? (data.catalogs || {}) : catalogs;
    if (includeCatalogs) {
      setCatalogs(nextCatalogs);
    }
    const equipment = data.equipment || {};
    const allItems = buildInventoryItemsFromPayload(data.inventory || {}, nextCatalogs);
    const items = allItems.filter((item) => !isGridItemEquipped(item, equipment));
    setInventorySlots(applyLayoutToGrid(items, data.layout));
    setMarket(data.market);
    setMarketName(data.market?.name || '');
    setMyMarketSlots(buildGridItems(data.items || [], MARKET_GRID_SIZE, nextCatalogs));
  }, [applyLayoutToGrid, buildGridItems, buildInventoryItemsFromPayload, catalogs]);

  const fetchMarketInit = useCallback(async () => {
    if (!username) {
      navigate('/login');
      return;
    }
    setIsRefreshing(true);
    setError('');
    try {
      const res = await axios.get(`/api/market/init?username=${encodeURIComponent(username)}`);
      if (!res.data?.success) {
        throw new Error(res.data?.message || tm('errorLoadFailed'));
      }
      applyMarketPayload(res.data, { includeCatalogs: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorLoadFailed'));
    } finally {
      setIsRefreshing(false);
    }
  }, [applyMarketPayload, navigate, setError, tm, username]);

  const fetchMarketState = useCallback(async () => {
    // Satın alma/listeleme sonrası yenileme arka planda; ekranı kaplayan perde sadece ilk açılışta.
    setError('');
    try {
      const res = await axios.get(`/api/market/state?username=${encodeURIComponent(username)}`);
      if (!res.data?.success) {
        throw new Error(res.data?.message || tm('errorFetchFailed'));
      }
      applyMarketPayload(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorLoadFailed'));
    } finally {
      setIsRefreshing(false);
    }
  }, [applyMarketPayload, setError, tm, username]);

  const {
    setActiveItemPopup,
    handleGridItemClick,
    buildPopupProps
  } = useInventoryItemPopupActions({
    username,
    onAfterAction: fetchMarketState,
    setError,
    setStatus,
    clearError,
    setDepotItemCount,
    refreshCharacter
  });

  const itemPopupProps = buildPopupProps();

  const loadMine = useCallback(async (nextCatalogs = catalogs) => {
    const res = await axios.get(`/api/market/my?username=${encodeURIComponent(username)}`);
    if (!res.data?.success) throw new Error(res.data?.message || tm('errorFetchFailed'));
    setMarket(res.data.market);
    setMarketName(res.data.market?.name || '');
    setMyMarketSlots(buildGridItems(res.data.items || [], MARKET_GRID_SIZE, nextCatalogs));
  }, [buildGridItems, catalogs, tm, username]);

  useEffect(() => {
    fetchMarketInit();
    // Initial load is intentionally keyed to the logged-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const toggleMarket = async () => {
    setError('');
    const opening = !market?.isOpen;
    try {
      if (opening) {
        const setupRes = await axios.post('/api/market/setup', { username, name: marketName });
        if (!setupRes.data?.success) {
          setError(setupRes.data?.message || tm('errorNameSaveFailed'));
          return;
        }
      }
      const res = await axios.post('/api/market/toggle', { username, isOpen: !market?.isOpen });
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorToggleFailed'));
        return;
      }
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorToggleFailed'));
    }
  };

  // Pazar süresi / izin geri sayımı; süre dolunca durumu yenile.
  useEffect(() => {
    const id = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 15000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (isMarketOpen && closesAt > 0 && closesAt <= nowSec) {
      loadMine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMarketOpen, closesAt, nowSec]);

  const transferGold = async () => {
    setError('');
    try {
      const res = await axios.post('/api/market/transfer-gold', { username });
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorTransferFailed'));
        return;
      }
      setStatus(tm('statusGoldTransferred', { amount: res.data.transferredGold || 0 }));
      await loadMine();
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorTransferFailed'));
    }
  };

  const openListModal = (slotIndex, sourceIndex = draggedInventoryIndex) => {
    if (isMarketOpen) {
      setError(tm('errorCannotAddWhileOpen'));
      return;
    }
    if (isPermitLockedSlot(slotIndex)) {
      setError(tm('permitRequired'));
      return;
    }
    if (sourceIndex === null) return;
    const item = inventorySlots[sourceIndex];
    if (!item || !canPlaceInGrid(myMarketSlots, slotIndex, item, MARKET_GRID_SIZE)) {
      setError(ti('errorDropBlocked'));
      return;
    }
    setPendingList({ item, slotIndex });
    setListPrice('');
    setListCount(1);
  };

  const submitListing = async () => {
    if (isMarketOpen) {
      setError(tm('errorCannotAddWhileOpen'));
      return;
    }
    const item = pendingList?.item;
    const price = Number(listPrice);
    if (!item || !Number.isInteger(price) || price < 1) {
      setError(tm('errorInvalidPrice'));
      return;
    }
    try {
      const res = await axios.post('/api/market/list', {
        username,
        inventoryType: item.inventoryType,
        userItemId: item.id,
        price,
        count: Math.min(Math.max(1, Number(listCount) || 1), item.count || 1),
        slotIndex: pendingList.slotIndex
      });
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorListFailed'));
        return;
      }
      setPendingList(null);
      setStatus(tm('statusListed'));
      await fetchMarketState();
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorListFailed'));
    } finally {
      setDraggedInventoryIndex(null);
      setDragOverMarketIndex(null);
    }
  };

  const unlistItem = async (listingId, inventorySlotIndex = -1) => {
    if (isMarketOpen) {
      setError(tm('errorCannotRemoveWhileOpen'));
      return;
    }
    try {
      const res = await axios.post('/api/market/unlist', { username, listingId, inventorySlotIndex });
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorUnlistFailed'));
        return;
      }
      setActivePopup(null);
      setStatus(tm('statusUnlisted'));
      await fetchMarketState();
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorUnlistFailed'));
    }
  };

  const handleInventoryDragStart = (event, index, item) => {
    if (!item || isMarketOpen) return;
    setActiveItemPopup(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/market-source', 'inventory');
    event.dataTransfer.setData('text/inventory-index', String(index));
    event.dataTransfer.setData('text/inventory-item-key', item.uniqueKey);
    setNativeDragGhost(event, buildItemImageUrl(item.baseItem?.imageUrl));
    setDraggedInventoryIndex(index);
    setDraggedMarketIndex(null);
  };

  const handleMarketDragStart = (event, index, item) => {
    if (!item || isMarketOpen) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/market-source', 'market');
    event.dataTransfer.setData('text/market-index', String(index));
    event.dataTransfer.setData('text/market-listing-id', String(item.id));
    setNativeDragGhost(event, buildItemImageUrl(item.baseItem?.imageUrl));
    setDraggedMarketIndex(index);
    setDraggedInventoryIndex(null);
  };

  const handleMarketDragOver = (event, index) => {
    if (isMarketOpen) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverMarketIndex(index);
  };

  const handleMarketDrop = (event, index) => {
    event.preventDefault();
    if (isMarketOpen) {
      setError(tm('errorCannotAddWhileOpen'));
      return;
    }
    const source = event.dataTransfer.getData('text/market-source');
    if (source !== 'inventory') return;
    const payloadIndex = Number(event.dataTransfer.getData('text/inventory-index'));
    if (Number.isInteger(payloadIndex)) {
      setDraggedInventoryIndex(payloadIndex);
    }
    const sourceIndex = Number.isInteger(payloadIndex) ? payloadIndex : draggedInventoryIndex;
    openListModal(index, sourceIndex);
  };

  const handleInventoryDragOver = (event, index) => {
    if (isMarketOpen) return;
    if (draggedMarketIndex !== null) {
      const item = myMarketSlots[draggedMarketIndex];
      if (!item) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = canPlaceInGrid(inventorySlots, index, item, INVENTORY_GRID_SIZE) ? 'move' : 'none';
      setDragOverInventoryIndex(index);
      return;
    }
    const sourceIndex = resolveDraggedInventoryIndex(event);
    if (sourceIndex === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = canMoveItemInGrid(
      inventorySlots,
      sourceIndex,
      index,
      INVENTORY_GRID_SIZE
    ) ? 'move' : 'none';
    setDragOverInventoryIndex(index);
  };

  const handleInventoryDrop = (event, index) => {
    event.preventDefault();
    if (isMarketOpen) {
      setError(tm('errorCannotRemoveWhileOpen'));
      return;
    }
    const source = event.dataTransfer.getData('text/market-source');
    if (source === 'market') {
      const payloadIndex = Number(event.dataTransfer.getData('text/market-index'));
      const marketIndex = Number.isInteger(payloadIndex) ? payloadIndex : draggedMarketIndex;
      const item = marketIndex !== null ? myMarketSlots[marketIndex] : null;
      if (!item) return;
      if (!canPlaceInGrid(inventorySlots, index, item, INVENTORY_GRID_SIZE)) {
        setError(ti('errorDropBlocked'));
        setDragOverInventoryIndex(null);
        setDraggedMarketIndex(null);
        return;
      }
      unlistItem(item.id, index);
      setDragOverInventoryIndex(null);
      setDraggedMarketIndex(null);
      return;
    }
    if (source === 'inventory') {
      const payloadIndex = Number(event.dataTransfer.getData('text/inventory-index'));
      if (Number.isInteger(payloadIndex)) {
        setDraggedInventoryIndex(payloadIndex);
      }
      const sourceIndex = resolveDraggedInventoryIndex(event);
      setDragOverInventoryIndex(null);
      setDraggedInventoryIndex(null);
      if (sourceIndex === null || sourceIndex === index) return;
      if (!canMoveItemInGrid(inventorySlots, sourceIndex, index, INVENTORY_GRID_SIZE)) {
        setError(ti('errorDropBlocked'));
        return;
      }
      const next = [...inventorySlots];
      const draggedItem = next[sourceIndex];
      next[sourceIndex] = null;
      next[index] = draggedItem;
      setInventorySlots(next);
      persistInventoryLayout(next);
    }
  };

  const getTouchTargetFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const marketSlot = elementAtPoint?.closest?.('[data-market-slot-index]');
    if (marketSlot) {
      const index = Number(marketSlot.dataset.marketSlotIndex);
      return Number.isInteger(index) ? { area: 'market', index } : null;
    }

    const inventorySlot = elementAtPoint?.closest?.('[data-slot-index]');
    if (inventorySlot?.closest?.('.market-inventory-grid-wrapper')) {
      const index = Number(inventorySlot.dataset.slotIndex);
      return Number.isInteger(index) ? { area: 'inventory', index } : null;
    }

    const pageTab = elementAtPoint?.closest?.('[data-inventory-page]');
    if (pageTab?.closest?.('.market-inventory-grid-wrapper, .market-page')) {
      const page = Number(pageTab.dataset.inventoryPage);
      return Number.isInteger(page) ? { area: 'inventoryPage', page } : null;
    }

    return null;
  };

  const handleTouchStart = (event, source, index, item) => {
    if (!item || isMarketOpen) return;
    const touch = event.touches?.[0];
    if (!touch) return;

    lockTouchDragScroll();

    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    touchDragSourceRef.current = { source, index, item };
    setActivePopup(null);
    setActiveItemPopup(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: buildItemImageUrl(item.baseItem?.imageUrl),
      x: touch.clientX,
      y: touch.clientY
    });

    if (source === 'inventory') {
      setDraggedInventoryIndex(index);
      setDraggedMarketIndex(null);
    } else {
      setDraggedMarketIndex(index);
      setDraggedInventoryIndex(null);
    }
  };

  const handleTouchMove = (event) => {
    const source = touchDragSourceRef.current;
    if (!source) return;
    const touch = event.touches?.[0];
    if (!touch) return;

    setTouchDragGhost((prev) => (prev ? {
      ...prev,
      x: touch.clientX,
      y: touch.clientY
    } : prev));

    const dx = Math.abs(touch.clientX - touchStartPointRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartPointRef.current.y);
    if (dx > 6 || dy > 6) {
      touchHasMovedRef.current = true;
    }

    const target = getTouchTargetFromPoint(touch.clientX, touch.clientY);
    if (target?.area === 'inventoryPage' && (source.source === 'market' || source.source === 'inventory')) {
      if (target.page !== currentInventoryPage) {
        setCurrentInventoryPage(target.page);
      }
      setDragOverMarketIndex(null);
      setDragOverInventoryIndex(null);
      return;
    }

    setDragOverMarketIndex(
      source.source === 'inventory' && target?.area === 'market' ? target.index : null
    );
    setDragOverInventoryIndex(
      target?.area === 'inventory' && (source.source === 'market' || source.source === 'inventory')
        ? target.index
        : null
    );
  };

  const clearTouchDragState = () => {
    unlockTouchDragScroll();
    touchHasMovedRef.current = false;
    touchDragSourceRef.current = null;
    setIsTouchDragging(false);
    setTouchDragGhost(null);
    setDragOverMarketIndex(null);
    setDragOverInventoryIndex(null);
    setDraggedInventoryIndex(null);
    setDraggedMarketIndex(null);
  };

  const handleTouchEnd = (event) => {
    const source = touchDragSourceRef.current;
    if (!source) return;

    const changedTouch = event.changedTouches?.[0];
    if (touchHasMovedRef.current && changedTouch) {
      const target = getTouchTargetFromPoint(changedTouch.clientX, changedTouch.clientY);
      if (source.source === 'inventory' && target?.area === 'market') {
        openListModal(target.index, source.index);
        skipNextClickRef.current = true;
      } else if (source.source === 'market' && target?.area === 'inventory') {
        const item = myMarketSlots[source.index];
        if (item) {
          if (!canPlaceInGrid(inventorySlots, target.index, item, INVENTORY_GRID_SIZE)) {
            setError(ti('errorDropBlocked'));
          } else {
            unlistItem(item.id, target.index);
          }
        }
        skipNextClickRef.current = true;
      } else if (source.source === 'inventory' && target?.area === 'inventory') {
        if (source.index !== target.index
          && canMoveItemInGrid(inventorySlots, source.index, target.index, INVENTORY_GRID_SIZE)) {
          const next = [...inventorySlots];
          const draggedItem = next[source.index];
          next[source.index] = null;
          next[target.index] = draggedItem;
          setInventorySlots(next);
          persistInventoryLayout(next);
        } else if (source.index !== target.index) {
          setError(ti('errorDropBlocked'));
        }
        skipNextClickRef.current = true;
      }
    }

    clearTouchDragState();
  };

  const loadMarkets = useCallback(async (reset = false) => {
    if (browseLoading || (!hasMoreMarkets && !reset)) return;
    setBrowseLoading(true);
    try {
      const offset = reset ? 0 : browseOffset;
      const res = await axios.get(
        `/api/market/browse?offset=${offset}&limit=10&username=${encodeURIComponent(username)}`
      );
      if (res.data?.success) {
        const receivedMarkets = res.data.markets || [];
        const ownMarketID = market?.id;
        const nextMarkets = ownMarketID
          ? receivedMarkets.filter((entry) => entry.id !== ownMarketID)
          : receivedMarkets;
        setBrowseMarkets((prev) => (reset ? nextMarkets : [...prev, ...nextMarkets]));
        setBrowseOffset(offset + receivedMarkets.length);
        setHasMoreMarkets(res.data.hasMore !== false && receivedMarkets.length > 0);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorBrowseFailed'));
    } finally {
      setBrowseLoading(false);
      if (reset) {
        setBrowseLoaded(true);
      }
    }
  }, [browseLoading, browseOffset, hasMoreMarkets, market?.id, setError, tm, username]);

  useEffect(() => {
    if (activeTab !== 'browse') {
      setBrowseLoaded(false);
      setBrowseMarkets([]);
      setBrowseOffset(0);
      setHasMoreMarkets(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'browse' && !browseLoaded) {
      loadMarkets(true);
    }
  }, [activeTab, browseLoaded, loadMarkets]);

  const handleBrowseScroll = () => {
    const node = browseRef.current;
    if (!node || browseLoading) return;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 60) {
      loadMarkets(false);
    }
  };

  const handleMarketItemClick = (event, mode, item, index) => {
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      return;
    }
    if (!item || !marketCardRef.current) {
      setActivePopup(null);
      return;
    }

    const isSameSlotOpen = activePopup && activePopup.mode === mode && activePopup.index === index;
    if (isSameSlotOpen) {
      setActivePopup(null);
      return;
    }

    setActivePopup({
      mode,
      index,
      item
    });
  };

  const openShop = async (marketId) => {
    setError('');
    try {
      const res = await axios.get(`/api/market/shop?marketId=${marketId}&username=${encodeURIComponent(username)}`);
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorShopOpenFailed'));
        return;
      }
      setSelectedShop(res.data.market);
      setBuyerGold(res.data.buyerGold || 0);
      setShopSlots(buildGridItems(res.data.items || [], MARKET_GRID_SIZE));
      setActivePopup(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorShopOpenFailed'));
    }
  };

  const buyItem = async (listingId) => {
    try {
      const res = await axios.post('/api/market/buy', { username, listingId });
      if (!res.data?.success) {
        setError(res.data?.message || tm('errorPurchaseFailed'));
        return;
      }
      setStatus(tm('statusPurchaseComplete'));
      setActivePopup(null);
      await Promise.all([
        selectedShop ? openShop(selectedShop.id) : Promise.resolve(),
        fetchMarketState()
      ]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || tm('errorPurchaseFailed'));
    }
  };

  const renderItem = (item) => (
    <>
      {item?.baseItem?.imageUrl ? (
        <span className="slot-item-image">
          <ItemImage imageUrl={item.baseItem.imageUrl} alt={item.baseItem.name} />
        </span>
      ) : null}
      {item?.count > 1 ? <span className="inventory-slot-count">x{item.count}</span> : null}
    </>
  );

  const renderPopup = () => {
    if (!activePopup?.item) return null;
    const item = activePopup.item;
    const rows = buildDetailedStatRows({
      ...item.baseItem,
      categoryLabel: item.categoryLabel,
      count: item.count
    });
    return (
      <div className="inventory-item-popup-backdrop" onClick={() => setActivePopup(null)}>
        <div className="inventory-item-popup inventory-item-popup--modal market-item-popup" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="inventory-item-popup-close"
            onClick={() => setActivePopup(null)}
            aria-label={tc('close')}
          >
            X
          </button>
          <div className="market-popup-content">
            <div className="market-popup-details">
              <h4>{item.baseItem?.name || tm('itemFallback')}</h4>
              <p>{tc('category')}: {item.categoryLabel}</p>
              {item.price ? <p>{tc('price')}: {tm('priceYang', { amount: item.price })}</p> : null}
              {rows.map((row, index) => (
                <p key={`${row.label}-${index}`}>{row.label}: {row.value}</p>
              ))}
            </div>
            {item.baseItem?.imageUrl ? (
              <div className="market-popup-image">
                <ItemImage
                  imageUrl={item.baseItem.imageUrl}
                  alt={item.baseItem?.name || tm('itemFallback')}
                />
              </div>
            ) : null}
          </div>
          {activePopup.mode === 'mine' ? (
            <button
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonSmallStyle}
              disabled={isMarketOpen}
              onClick={() => unlistItem(item.id)}
            >
              {tm('removeFromStall')}
            </button>
          ) : null}
          {activePopup.mode === 'shop' ? (
            <button
              type="button"
              className="auth-button auth-button--compact"
              style={authButtonSmallStyle}
              disabled={!item.canAfford}
              onClick={() => buyItem(item.id)}
            >
              {tc('buy')}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const getGridCellPosition = (index, pageStart = 0) => {
    const localIndex = index - pageStart;
    return {
      colStart: (localIndex % GRID_COLUMNS) + 1,
      rowStart: Math.floor(localIndex / GRID_COLUMNS) + 1
    };
  };

  const buildGridSlotStyle = (index, pageStart, span) => {
    const { colStart, rowStart } = getGridCellPosition(index, pageStart);
    return {
      gridColumnStart: colStart,
      gridRowStart: rowStart,
      ...(span ? {
        gridColumnEnd: `span ${span.width}`,
        gridRowEnd: `span ${span.height}`,
        aspectRatio: `${span.width} / ${span.height}`,
        zIndex: 2
      } : {})
    };
  };

  const isMarketInventoryDropBlocked = (index) => {
    if (draggedMarketIndex !== null) {
      const item = myMarketSlots[draggedMarketIndex];
      if (!item) return true;
      return !canPlaceInGrid(inventorySlots, index, item, INVENTORY_GRID_SIZE);
    }
    if (draggedInventoryIndex !== null) {
      return !canMoveItemInGrid(
        inventorySlots,
        draggedInventoryIndex,
        index,
        INVENTORY_GRID_SIZE
      );
    }
    return false;
  };

  const handleMarketInventoryClick = (event, slotItem, index) => {
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false;
      return;
    }
    setActivePopup(null);
    handleGridItemClick(slotItem, index, inventoryGridAreaRef);
  };

  const renderMarketSlotGrid = (slots, mode) => {
    const { coveredIndexes, spanByIndex } = computePageCoverage(slots, 0, MARKET_GRID_SIZE);
    return (
      <div className="inventory-grid-wrapper market-grid-wrapper">
        <div className="inventory-grid-area">
          <div className="market-grid inventory-grid-10x10">
            {slots.map((item, index) => {
              const { colStart, rowStart } = getGridCellPosition(index);
              if (coveredIndexes.has(index)) {
                return null;
              }
              const span = spanByIndex[index];
              const isMine = mode === 'mine';
              const isShop = mode === 'shop';
              return (
                <div
                  key={`${mode}-market-${index}`}
                  className={`inventory-slot-box market-slot ${item ? 'filled' : 'empty'} ${isMine && isMarketOpen ? 'market-slot--locked' : ''} ${isMine && isPermitLockedSlot(index) ? 'market-slot--permit-locked' : ''} ${span ? 'multi-size' : ''} ${dragOverMarketIndex === index ? 'drop-target' : ''} ${isShop && item ? (item.canAfford ? 'market-slot--affordable' : 'market-slot--unaffordable') : ''}`}
                  data-market-slot-index={index}
                  style={buildGridSlotStyle(index, 0, span)}
                  draggable={isMine && !isMarketOpen && !!item}
                  onDragStart={(event) => isMine && handleMarketDragStart(event, index, item)}
                  onDragOver={(event) => isMine && handleMarketDragOver(event, index)}
                  onDragLeave={() => isMine && setDragOverMarketIndex(null)}
                  onDrop={(event) => isMine && handleMarketDrop(event, index)}
                  onTouchStart={(event) => isMine && !isMarketOpen && handleTouchStart(event, 'market', index, item)}
                  onClick={(event) => handleMarketItemClick(event, mode, item, index)}
                >
                  {renderItem(item)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="auth-container auth-container--selection market-page">
      <AuthLayeredCard
        className={`market-card${isTouchDragging ? ' market-card--touch-dragging' : ''}`}
        style={marketMenuBgCssVars}
        ref={marketCardRef}
        onTouchMoveCapture={handleTouchMove}
        onTouchEndCapture={handleTouchEnd}
        onTouchCancelCapture={clearTouchDragState}
      >
        {isRefreshing ? (
          <div className="market-loading-overlay" aria-busy="true" aria-live="polite">
            <span className="market-loading-text">{tc('loading')}</span>
          </div>
        ) : null}
        <div className="battle-nav-row">
          <button type="button" className="auth-button auth-button--compact battle-nav-btn battle-nav-btn--back" style={authButtonSmallStyle} onClick={() => navigate('/game')} aria-label={tc('back')}>
            ←
          </button>
          <button type="button" className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'mine' ? ' battle-subtab--active' : ''}`} style={authButtonSmallStyle} onClick={() => setActiveTab('mine')}>
            {tm('tabsMine')}
          </button>
          <button type="button" className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'browse' ? ' battle-subtab--active' : ''}`} style={authButtonSmallStyle} onClick={() => setActiveTab('browse')}>
            {tm('tabsBrowse')}
          </button>
        </div>

        {activeTab === 'mine' ? (
          <div className="market-layout">
            <div className="market-controls">
              <div className="market-controls-row">
                <input
                  className="market-name-input"
                  value={marketName}
                  maxLength={30}
                  onChange={(event) => setMarketName(event.target.value)}
                  placeholder={tm('namePlaceholder')}
                />
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(market?.isOpen)}
                  className={`market-switch${market?.isOpen ? ' market-switch--on' : ''}`}
                  onClick={toggleMarket}
                  title={market?.isOpen ? tc('close') : tc('open')}
                >
                  <span className="market-switch__knob" aria-hidden="true" />
                  <span className="market-switch__text">
                    {market?.isOpen
                      ? (openLeftSec > 0 ? (openLeftSec >= 3600 ? tm('switchOpenLeftHours', { hours: Math.ceil(openLeftSec / 3600) }) : tm('switchOpenLeft', { min: Math.max(1, Math.ceil(openLeftSec / 60)) })) : tm('switchOpen'))
                      : tm('switchClosed')}
                  </span>
                </button>
              </div>
              <div className="market-controls-row market-controls-row--gold">
                <span className="market-gold">{tm('pendingGold', { amount: market?.pendingGold || 0 })}</span>
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={transferGold}>
                  {tc('transfer')}
                </button>
              </div>
            </div>

            <h2>{tm('stallTitle')}</h2>
            {renderMarketSlotGrid(myMarketSlots, 'mine')}
            <p className={`market-permit-line${permitActive ? ' market-permit-line--on' : ''}`}>
              {permitActive
                ? tm('permitActive', { days: Math.max(1, Math.ceil((permitUntil - nowSec) / 86400)) })
                : tm('permitHint')}
            </p>

            <h2>{tm('inventoryTitle')}</h2>
            <InventoryBagGrid
              gridSlots={inventorySlots}
              currentInventoryPage={currentInventoryPage}
              onPageChange={setCurrentInventoryPage}
              unlockedSlotsCount={unlockedSlotsCount}
              dragOverIndex={dragOverInventoryIndex}
              isDropBlocked={isMarketInventoryDropBlocked}
              draggable={(slotItem) => !isMarketOpen && !!slotItem}
              onDragStart={(event, index, item) => handleInventoryDragStart(event, index, item)}
              onDragOver={handleInventoryDragOver}
              onDragLeave={(index) => setDragOverInventoryIndex((prev) => (prev === index ? null : prev))}
              onDrop={handleInventoryDrop}
              onDragEnd={() => {
                setDragOverInventoryIndex(null);
                setDraggedInventoryIndex(null);
              }}
              onTouchStart={(event, index, item) => !isMarketOpen && handleTouchStart(event, 'inventory', index, item)}
              onClick={handleMarketInventoryClick}
              slotKeyPrefix="market-inventory"
              wrapperClassName="market-grid-wrapper market-inventory-grid-wrapper"
              gridClassName="market-grid inventory-grid-10x10"
              areaRef={inventoryGridAreaRef}
              slotExtraClassName="market-slot"
              emptySlotTitle={ti('slotEmpty')}
              lockedSlotTitle={ti('slotLocked')}
              pageTabsClassName="inventory-page-tabs market-page-tabs"
              isTouchDragging={isTouchDragging}
            />
          </div>
        ) : (
          <div className="market-layout">
            {selectedShop ? (
              <>
                <div className="market-shop-header">
                  <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => { setSelectedShop(null); setActivePopup(null); }}>
                    {tm('backToMarkets')}
                  </button>
                  <h2>{selectedShop.name}</h2>
                  <span className="market-gold">{tm('myGold', { amount: buyerGold })}</span>
                </div>
                {renderMarketSlotGrid(shopSlots, 'shop')}
              </>
            ) : (
              <>
                <h2>{tm('browseTitle')}</h2>
                <div className="market-browse-list" ref={browseRef} onScroll={handleBrowseScroll}>
                  {browseMarkets.map((entry, index) => (
                    <button key={`${entry.id}-${index}`} type="button" className="market-browse-item" onClick={() => openShop(entry.id)}>
                      {entry.name}
                    </button>
                  ))}
                  {(!browseLoaded && browseLoading) || (browseLoaded && browseLoading && browseMarkets.length > 0) ? (
                    <div className="market-list-status">{tc('loading')}</div>
                  ) : null}
                  {browseLoaded && !browseLoading && browseMarkets.length === 0 ? (
                    <div className="market-list-status">{tm('noMarkets')}</div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        )}

        {renderPopup()}
        {itemPopupProps ? <InventoryItemPopup {...itemPopupProps} /> : null}

        {pendingList ? (
          <div className="market-modal-backdrop">
            <div className="market-modal">
              <h3>{pendingList.item.baseItem?.name}</h3>
              <label>
                {tc('price')}
                <input type="number" min="1" value={listPrice} onChange={(event) => setListPrice(event.target.value)} />
              </label>
              {(pendingList.item.inventoryType === 'pots' || pendingList.item.inventoryType === 'others') ? (
                <label>
                  {tc('count')}
                  <input
                    type="number"
                    min="1"
                    max={pendingList.item.count || 1}
                    value={listCount}
                    onChange={(event) => setListCount(event.target.value)}
                  />
                </label>
              ) : null}
              <div className="market-modal-actions">
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => setPendingList(null)}>
                  {tc('cancel')}
                </button>
                <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={submitListing}>
                  {tm('listOnStall')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {touchDragGhost ? (
          <div
            className="touch-drag-ghost"
            style={{ left: `${touchDragGhost.x}px`, top: `${touchDragGhost.y}px` }}
          >
            {touchDragGhost.imageUrl ? (
              <img src={touchDragGhost.imageUrl} alt="" draggable={false} />
            ) : null}
          </div>
        ) : null}
      </AuthLayeredCard>
    </div>
  );
};

export default Market;
